import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import { authFetch } from "../../../lib/authFetch";

type DriverAdminDetailModalProps = {
  open: boolean;
  token: string | null;
  driverId: number | null;
  userId?: number | null;
  username?: string;
  onClose: () => void;
  onUpdated?: () => Promise<void> | void;
};

type DriverResponse = {
  ok?: boolean;
  error?: string;
  driver?: Record<string, unknown>;
  drivers?: Array<Record<string, unknown>>;
  data?: Record<string, unknown> | Array<Record<string, unknown>>;
  result?: Record<string, unknown> | Array<Record<string, unknown>>;
};

type DriverExtraResponse = {
  ok?: boolean;
  error?: string;
  extra?: Record<string, unknown>;
  data?: Record<string, unknown> | Array<Record<string, unknown>>;
  result?: Record<string, unknown> | Array<Record<string, unknown>>;
};

type DeliveriesResponse = {
  ok?: boolean;
  error?: string;
  deliveries?: Array<Record<string, unknown>> | Record<string, unknown>;
  data?: Array<Record<string, unknown>> | Record<string, unknown>;
  result?: Array<Record<string, unknown>> | Record<string, unknown>;
};

type DevicesResponse = {
  ok?: boolean;
  error?: string;
  devices?: Array<Record<string, unknown>> | Record<string, unknown>;
  data?: Array<Record<string, unknown>> | Record<string, unknown>;
  result?: Array<Record<string, unknown>> | Record<string, unknown>;
};

type GenericResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
};

type DriverGeneralForm = {
  tariffID: string;
  vehiculo: string;
  patente: string;
  DNI: string;
  CUIL: string;
  alias: string;
  CBU: string;
  comision: string;
};

type DriverExtraForm = {
  DNI_frente: string;
  DNI_dorso: string;
  cedula_frente: string;
  cedula_dorso: string;
  registro_frente: string;
  registro_dorso: string;
  selfie: string;
};

const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 120,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
};

const backdropStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
};

const shellStyle: React.CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: "1200px",
  maxHeight: "88vh",
  overflow: "hidden",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "18px 20px",
  borderBottom: "1px solid var(--border)",
};

const bodyStyle: React.CSSProperties = {
  padding: "20px",
  maxHeight: "76vh",
  overflowY: "auto",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "16px",
};

const tripleGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "16px",
};

const tabRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginBottom: "20px",
};

const infoBoxStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "16px",
  background: "rgba(255,255,255,0.03)",
  padding: "14px",
};

const mutedStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
};

const valueStyle: React.CSSProperties = {
  marginTop: "6px",
  fontSize: "14px",
  color: "var(--text-primary)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "48px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 14px",
  fontSize: "14px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.02)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
};

const tdStyle: React.CSSProperties = {
  padding: "12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "top",
};

const commissionOptions = [
  { label: "30%", value: "0.30" },
  { label: "25%", value: "0.25" },
  { label: "20%", value: "0.20" },
  { label: "15%", value: "0.15" },
  { label: "10%", value: "0.10" },
  { label: "5%", value: "0.05" },
];

function TabButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minHeight: "42px",
        padding: "0 16px",
        borderRadius: "999px",
        border: active ? "1px solid var(--border)" : "1px solid transparent",
        background: active ? "var(--primary-soft)" : "transparent",
        color: active ? "var(--text-primary)" : "var(--text-secondary)",
        fontSize: "14px",
        fontWeight: active ? 700 : 600,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "block",
        marginBottom: "8px",
        fontSize: "12px",
        color: "var(--text-secondary)",
      }}
    >
      {children}
    </span>
  );
}

function SelectField({
  label,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  return (
    <label style={{ display: "block" }}>
      <FieldLabel>{label}</FieldLabel>
      <select {...props} style={selectStyle}>
        {children}
      </select>
    </label>
  );
}

function firstObject<T = Record<string, unknown>>(value: unknown): T | null {
  if (!value) return null;
  if (Array.isArray(value)) return (value[0] as T) || null;
  if (typeof value === "object") return value as T;
  return null;
}

function normalizeDriver(payload: DriverResponse): Record<string, unknown> | null {
  return (
    firstObject(payload.driver) ||
    firstObject(payload.drivers) ||
    firstObject(payload.data) ||
    firstObject(payload.result)
  );
}

function normalizeExtra(payload: DriverExtraResponse): Record<string, unknown> | null {
  return (
    firstObject(payload.extra) ||
    firstObject(payload.data) ||
    firstObject(payload.result)
  );
}

function normalizeArray(
  payload: Record<string, unknown> | null | undefined
): Record<string, unknown>[] {
  if (!payload) return [];

  const source =
    payload.deliveries ||
    payload.devices ||
    payload.data ||
    payload.result;

  if (Array.isArray(source)) return source as Record<string, unknown>[];
  if (source && typeof source === "object") return [source as Record<string, unknown>];
  return [];
}

function asString(value: unknown): string {
  if (value == null) return "";
  return String(value);
}

function formatDate(value?: unknown): string {
  if (!value) return "—";
  const raw = String(value);
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("es-AR");
}

export default function DriverAdminDetailModal({
  open,
  token,
  driverId,
  userId,
  username,
  onClose,
  onUpdated,
}: DriverAdminDetailModalProps) {
  const [tab, setTab] = useState<"GENERAL" | "DOCUMENTACION" | "ENVIOS" | "DEVICES" | "SEGURIDAD">(
    "GENERAL"
  );

  const [loading, setLoading] = useState(false);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingExtra, setSavingExtra] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [driverRaw, setDriverRaw] = useState<Record<string, unknown> | null>(null);
  const [deliveries, setDeliveries] = useState<Record<string, unknown>[]>([]);
  const [devices, setDevices] = useState<Record<string, unknown>[]>([]);

  const [generalForm, setGeneralForm] = useState<DriverGeneralForm>({
    tariffID: "",
    vehiculo: "",
    patente: "",
    DNI: "",
    CUIL: "",
    alias: "",
    CBU: "",
    comision: "",
  });

  const [extraForm, setExtraForm] = useState<DriverExtraForm>({
    DNI_frente: "",
    DNI_dorso: "",
    cedula_frente: "",
    cedula_dorso: "",
    registro_frente: "",
    registro_dorso: "",
    selfie: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
  });

  const displayTitle = useMemo(() => {
    const alias = asString(driverRaw?.alias).trim();
    if (alias) return alias;
    if (username) return username;
    return driverId ? `Chofer #${driverId}` : "Detalle de chofer";
  }, [driverId, driverRaw, username]);

  const loadAll = useCallback(async () => {
    if (!open || !token || !driverId) return;

    setLoading(true);
    setError(null);

    try {
      const requests: Promise<Response>[] = [
        authFetch(`/v2/drivers/${driverId}`, { token }),
        authFetch(`/v2/drivers/${driverId}/extra`, { token }),
        authFetch(`/v2/drivers/${driverId}/deliveries`, { token }),
      ];

      if (userId) {
        requests.push(authFetch(`/v2/users/${userId}/devices`, { token }));
      }

      const responses = await Promise.all(requests);

      const driverRes = responses[0];
      const extraRes = responses[1];
      const deliveriesRes = responses[2];
      const devicesRes = responses[3];

      const driverJson: DriverResponse = await driverRes.json().catch(() => ({}));
      const extraJson: DriverExtraResponse = await extraRes.json().catch(() => ({}));
      const deliveriesJson: DeliveriesResponse = await deliveriesRes.json().catch(() => ({}));

      if (!driverRes.ok || driverJson?.ok === false) {
        throw new Error(driverJson?.error || "No se pudo cargar el driver");
      }

      const driver = normalizeDriver(driverJson);
      const extra = extraRes.ok && extraJson?.ok !== false ? normalizeExtra(extraJson) : null;
      const driverDeliveries = deliveriesRes.ok && deliveriesJson?.ok !== false
        ? normalizeArray(deliveriesJson)
        : [];

      let userDevices: Record<string, unknown>[] = [];
      if (devicesRes) {
        const devicesJson: DevicesResponse = await devicesRes.json().catch(() => ({}));
        if (devicesRes.ok && devicesJson?.ok !== false) {
          userDevices = normalizeArray(devicesJson);
        }
      }

      setDriverRaw(driver);
      setDeliveries(driverDeliveries);
      setDevices(userDevices);

      setGeneralForm({
        tariffID:
          asString(driver?.tarifaID ?? driver?.tariffID ?? driver?.tarifa_id ?? driver?.tariff_id) ||
          "",
        vehiculo: asString(driver?.vehiculo),
        patente: asString(driver?.patente),
        DNI: asString(driver?.DNI),
        CUIL: asString(driver?.CUIL),
        alias: asString(driver?.alias),
        CBU: asString(driver?.CBU),
        comision: asString(driver?.comision),
      });

      setExtraForm({
        DNI_frente: asString(extra?.DNI_frente),
        DNI_dorso: asString(extra?.DNI_dorso),
        cedula_frente: asString(extra?.cedula_frente),
        cedula_dorso: asString(extra?.cedula_dorso),
        registro_frente: asString(extra?.registro_frente),
        registro_dorso: asString(extra?.registro_dorso),
        selfie: asString(extra?.selfie),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error cargando detalle del chofer");
      setDriverRaw(null);
      setDeliveries([]);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [open, token, driverId, userId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSaveGeneral = async () => {
    if (!token || !driverId) return;

    setSavingGeneral(true);
    setError(null);

    try {
      const payload = {
        tarifaID: generalForm.tariffID ? Number(generalForm.tariffID) : null,
        vehiculo: generalForm.vehiculo.trim(),
        patente: generalForm.patente.trim(),
        DNI: generalForm.DNI.trim(),
        CUIL: generalForm.CUIL.trim(),
        alias: generalForm.alias.trim(),
        CBU: generalForm.CBU.trim(),
        comision: Number(generalForm.comision || "0"),
      };

      const response = await authFetch(`/v2/drivers/${driverId}`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: GenericResponse = await response.json().catch(() => ({}));

      if (!response.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo guardar el driver");
      }

      if (generalForm.tariffID) {
        const tariffResponse = await authFetch(`/v2/drivers/${driverId}/tariff`, {
          method: "PUT",
          token,
          json: true,
          body: JSON.stringify({ tarifaID: Number(generalForm.tariffID) }),
        });

        const tariffJson: GenericResponse = await tariffResponse.json().catch(() => ({}));
        if (!tariffResponse.ok || tariffJson?.ok === false) {
          throw new Error(tariffJson?.error || "No se pudo actualizar la tarifa");
        }
      }

      await loadAll();
      await onUpdated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error guardando datos generales");
    } finally {
      setSavingGeneral(false);
    }
  };

  const handleSaveExtra = async () => {
    if (!token || !driverId) return;

    setSavingExtra(true);
    setError(null);

    try {
      const payload = {
        photos: {
          DNI_frente: extraForm.DNI_frente.trim(),
          DNI_dorso: extraForm.DNI_dorso.trim(),
          cedula_frente: extraForm.cedula_frente.trim(),
          cedula_dorso: extraForm.cedula_dorso.trim(),
          registro_frente: extraForm.registro_frente.trim(),
          registro_dorso: extraForm.registro_dorso.trim(),
          selfie: extraForm.selfie.trim(),
        },
      };

      const response = await authFetch(`/v2/drivers/${driverId}/extra`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: GenericResponse = await response.json().catch(() => ({}));

      if (!response.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo guardar la documentación");
      }

      await loadAll();
      await onUpdated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error guardando documentación");
    } finally {
      setSavingExtra(false);
    }
  };

  const handleChangePassword = async () => {
    if (!token || !userId) return;
    if (!passwordForm.newPassword.trim()) {
      alert("Completá la nueva contraseña.");
      return;
    }

    setSavingPassword(true);
    setError(null);

    try {
      const response = await authFetch(`/v2/users/${userId}/change-password`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({
          password: passwordForm.newPassword,
        }),
      });

      const json: GenericResponse = await response.json().catch(() => ({}));

      if (!response.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo cambiar la contraseña");
      }

      setPasswordForm({ newPassword: "" });
      alert("Contraseña actualizada correctamente.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error cambiando contraseña");
    } finally {
      setSavingPassword(false);
    }
  };

  if (!open) return null;

  return (
    <div style={modalOverlayStyle}>
      <div style={backdropStyle} onClick={onClose} />

      <div style={shellStyle}>
        <Card style={{ padding: 0 }}>
          <div style={headerStyle}>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "28px",
                  fontWeight: 800,
                  color: "var(--text-primary)",
                }}
              >
                {displayTitle}
              </h2>
              <p
                style={{
                  margin: "6px 0 0 0",
                  fontSize: "14px",
                  color: "var(--text-secondary)",
                }}
              >
                Administración completa del chofer desde el panel admin.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-secondary)",
                fontSize: "22px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          <div style={bodyStyle}>
            <div style={tabRowStyle}>
              <TabButton active={tab === "GENERAL"} onClick={() => setTab("GENERAL")}>
                General
              </TabButton>
              <TabButton
                active={tab === "DOCUMENTACION"}
                onClick={() => setTab("DOCUMENTACION")}
              >
                Documentación
              </TabButton>
              <TabButton active={tab === "ENVIOS"} onClick={() => setTab("ENVIOS")}>
                Envíos
              </TabButton>
              <TabButton active={tab === "DEVICES"} onClick={() => setTab("DEVICES")}>
                Devices
              </TabButton>
              <TabButton active={tab === "SEGURIDAD"} onClick={() => setTab("SEGURIDAD")}>
                Seguridad
              </TabButton>
            </div>

            {error ? (
              <div style={{ marginBottom: "16px", color: "var(--danger)", fontSize: "14px" }}>
                {error}
              </div>
            ) : null}

            {loading ? (
              <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                Cargando detalle del chofer...
              </div>
            ) : null}

            {!loading && tab === "GENERAL" ? (
              <div style={{ display: "grid", gap: "20px" }}>
                <div style={tripleGridStyle} className="driver-admin-summary-grid">
                  <div style={infoBoxStyle}>
                    <div style={mutedStyle}>Driver ID</div>
                    <div style={valueStyle}>{driverId ?? "—"}</div>
                  </div>

                  <div style={infoBoxStyle}>
                    <div style={mutedStyle}>User ID</div>
                    <div style={valueStyle}>{userId ?? "—"}</div>
                  </div>

                  <div style={infoBoxStyle}>
                    <div style={mutedStyle}>Username</div>
                    <div style={valueStyle}>{username || "—"}</div>
                  </div>
                </div>

                <Card>
                  <div style={{ marginBottom: "16px" }}>
                    <h3 style={sectionTitleStyle}>Datos generales</h3>
                  </div>

                  <div style={gridStyle} className="driver-admin-grid">
                    <div>
                      <FieldLabel>Tarifa ID</FieldLabel>
                      <Input
                        value={generalForm.tariffID}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, tariffID: e.target.value }))
                        }
                        placeholder="1"
                      />
                    </div>

                    <div>
                      <FieldLabel>Vehículo</FieldLabel>
                      <Input
                        value={generalForm.vehiculo}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, vehiculo: e.target.value }))
                        }
                        placeholder="Moto / Auto / Utilitario"
                      />
                    </div>

                    <div>
                      <FieldLabel>Patente</FieldLabel>
                      <Input
                        value={generalForm.patente}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, patente: e.target.value }))
                        }
                        placeholder="A123BCD"
                      />
                    </div>

                    <div>
                      <FieldLabel>DNI</FieldLabel>
                      <Input
                        value={generalForm.DNI}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, DNI: e.target.value }))
                        }
                        placeholder="30111222"
                      />
                    </div>

                    <div>
                      <FieldLabel>CUIL</FieldLabel>
                      <Input
                        value={generalForm.CUIL}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, CUIL: e.target.value }))
                        }
                        placeholder="20-30111222-3"
                      />
                    </div>

                    <div>
                      <FieldLabel>Alias</FieldLabel>
                      <Input
                        value={generalForm.alias}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, alias: e.target.value }))
                        }
                        placeholder="driver.alias"
                      />
                    </div>

                    <div>
                      <FieldLabel>CBU</FieldLabel>
                      <Input
                        value={generalForm.CBU}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, CBU: e.target.value }))
                        }
                        placeholder="2850590940090418135201"
                      />
                    </div>

                    <div>
                      <FieldLabel>Comisión (decimal)</FieldLabel>
                      <Input
                        value={generalForm.comision}
                        onChange={(e) =>
                          setGeneralForm((prev) => ({ ...prev, comision: e.target.value }))
                        }
                        placeholder="0.25"
                      />
                    </div>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <FieldLabel>Comisión rápida</FieldLabel>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {commissionOptions.map((option) => (
                          <Button
                            key={option.value}
                            type="button"
                            variant={
                              generalForm.comision === option.value ? "primary" : "secondary"
                            }
                            onClick={() =>
                              setGeneralForm((prev) => ({ ...prev, comision: option.value }))
                            }
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                      <div style={{ ...mutedStyle, marginTop: "10px" }}>
                        Se envía en formato decimal. Ejemplo: 25% → 0.25
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: "24px",
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button variant="secondary" onClick={loadAll} disabled={savingGeneral}>
                      Recargar
                    </Button>
                    <Button onClick={handleSaveGeneral} disabled={savingGeneral}>
                      {savingGeneral ? "Guardando..." : "Guardar datos"}
                    </Button>
                  </div>
                </Card>
              </div>
            ) : null}

            {!loading && tab === "DOCUMENTACION" ? (
              <Card>
                <div style={{ marginBottom: "16px" }}>
                  <h3 style={sectionTitleStyle}>Documentación / driver_extra</h3>
                </div>

                <div style={gridStyle} className="driver-admin-grid">
                  <div>
                    <FieldLabel>DNI frente</FieldLabel>
                    <Input
                      value={extraForm.DNI_frente}
                      onChange={(e) =>
                        setExtraForm((prev) => ({ ...prev, DNI_frente: e.target.value }))
                      }
                      placeholder="URL o referencia"
                    />
                  </div>

                  <div>
                    <FieldLabel>DNI dorso</FieldLabel>
                    <Input
                      value={extraForm.DNI_dorso}
                      onChange={(e) =>
                        setExtraForm((prev) => ({ ...prev, DNI_dorso: e.target.value }))
                      }
                      placeholder="URL o referencia"
                    />
                  </div>

                  <div>
                    <FieldLabel>Cédula frente</FieldLabel>
                    <Input
                      value={extraForm.cedula_frente}
                      onChange={(e) =>
                        setExtraForm((prev) => ({ ...prev, cedula_frente: e.target.value }))
                      }
                      placeholder="URL o referencia"
                    />
                  </div>

                  <div>
                    <FieldLabel>Cédula dorso</FieldLabel>
                    <Input
                      value={extraForm.cedula_dorso}
                      onChange={(e) =>
                        setExtraForm((prev) => ({ ...prev, cedula_dorso: e.target.value }))
                      }
                      placeholder="URL o referencia"
                    />
                  </div>

                  <div>
                    <FieldLabel>Registro frente</FieldLabel>
                    <Input
                      value={extraForm.registro_frente}
                      onChange={(e) =>
                        setExtraForm((prev) => ({ ...prev, registro_frente: e.target.value }))
                      }
                      placeholder="URL o referencia"
                    />
                  </div>

                  <div>
                    <FieldLabel>Registro dorso</FieldLabel>
                    <Input
                      value={extraForm.registro_dorso}
                      onChange={(e) =>
                        setExtraForm((prev) => ({ ...prev, registro_dorso: e.target.value }))
                      }
                      placeholder="URL o referencia"
                    />
                  </div>

                  <div style={{ gridColumn: "1 / -1" }}>
                    <FieldLabel>Selfie</FieldLabel>
                    <Input
                      value={extraForm.selfie}
                      onChange={(e) =>
                        setExtraForm((prev) => ({ ...prev, selfie: e.target.value }))
                      }
                      placeholder="URL o referencia"
                    />
                  </div>
                </div>

                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <Button variant="secondary" onClick={loadAll} disabled={savingExtra}>
                    Recargar
                  </Button>
                  <Button onClick={handleSaveExtra} disabled={savingExtra}>
                    {savingExtra ? "Guardando..." : "Guardar documentación"}
                  </Button>
                </div>
              </Card>
            ) : null}

            {!loading && tab === "ENVIOS" ? (
              <Card>
                <div style={{ marginBottom: "16px" }}>
                  <h3 style={sectionTitleStyle}>Envíos del chofer</h3>
                </div>

                {deliveries.length === 0 ? (
                  <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                    Sin envíos cargados o el endpoint no devolvió resultados.
                  </div>
                ) : (
                  <div style={tableWrapStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>ID</th>
                          <th style={thStyle}>Tracking</th>
                          <th style={thStyle}>Estado</th>
                          <th style={thStyle}>Dirección</th>
                          <th style={thStyle}>Actualizado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveries.map((delivery, index) => (
                          <tr key={`${delivery.id || index}`}>
                            <td style={tdStyle}>{asString(delivery.id) || "—"}</td>
                            <td style={tdStyle}>
                              {asString(delivery.tracking || delivery.numero_tracking) || "—"}
                            </td>
                            <td style={tdStyle}>
                              {asString(delivery.status || delivery.estado || delivery.status_ml) || "—"}
                            </td>
                            <td style={tdStyle}>
                              {asString(delivery.direccion || delivery.address || delivery.localidad) || "—"}
                            </td>
                            <td style={tdStyle}>
                              {formatDate(delivery.updated_at || delivery.fecha_estado)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            ) : null}

            {!loading && tab === "DEVICES" ? (
              <Card>
                <div style={{ marginBottom: "16px" }}>
                  <h3 style={sectionTitleStyle}>Devices del usuario</h3>
                </div>

                {!userId ? (
                  <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                    Este modal no recibió `userId`, por eso no puede cargar devices.
                  </div>
                ) : devices.length === 0 ? (
                  <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                    Sin devices asociados o sin datos disponibles todavía.
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: "14px" }}>
                    {devices.map((device, index) => (
                      <div key={`${device.id || index}`} style={infoBoxStyle}>
                        <div style={gridStyle} className="driver-admin-grid">
                          <div>
                            <div style={mutedStyle}>Device ID</div>
                            <div style={valueStyle}>{asString(device.id) || "—"}</div>
                          </div>
                          <div>
                            <div style={mutedStyle}>Token</div>
                            <div style={valueStyle}>
                              {asString(device.token || device.device_token) || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={mutedStyle}>Batería</div>
                            <div style={valueStyle}>
                              {asString(device.battery || device.battery_level) || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={mutedStyle}>Estado</div>
                            <div style={valueStyle}>
                              {asString(device.active ?? device.estado ?? device.status) || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={mutedStyle}>Latitud</div>
                            <div style={valueStyle}>
                              {asString(device.latitude || device.lat) || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={mutedStyle}>Longitud</div>
                            <div style={valueStyle}>
                              {asString(device.longitude || device.lng) || "—"}
                            </div>
                          </div>
                          <div style={{ gridColumn: "1 / -1" }}>
                            <div style={mutedStyle}>Última actualización</div>
                            <div style={valueStyle}>
                              {formatDate(device.updated_at || device.last_seen || device.last_update)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ) : null}

            {!loading && tab === "SEGURIDAD" ? (
              <Card>
                <div style={{ marginBottom: "16px" }}>
                  <h3 style={sectionTitleStyle}>Seguridad / contraseña</h3>
                </div>

                {!userId ? (
                  <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                    Este modal no recibió `userId`, por eso no puede cambiar contraseña.
                  </div>
                ) : (
                  <div style={gridStyle} className="driver-admin-grid">
                    <div>
                      <FieldLabel>Username</FieldLabel>
                      <Input value={username || ""} disabled />
                    </div>

                    <div>
                      <FieldLabel>Nueva contraseña</FieldLabel>
                      <Input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) =>
                          setPasswordForm({ newPassword: e.target.value })
                        }
                        placeholder="Ingresá una nueva contraseña"
                      />
                    </div>
                  </div>
                )}

                {userId ? (
                  <div
                    style={{
                      marginTop: "24px",
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button onClick={handleChangePassword} disabled={savingPassword}>
                      {savingPassword ? "Guardando..." : "Cambiar contraseña"}
                    </Button>
                  </div>
                ) : null}
              </Card>
            ) : null}

            <style>
              {`
                @media (max-width: 920px) {
                  .driver-admin-grid,
                  .driver-admin-summary-grid {
                    grid-template-columns: 1fr !important;
                  }
                }
              `}
            </style>
          </div>
        </Card>
      </div>
    </div>
  );
}